import api from './api';
import { ExercisePattern, Exercise, PatternRedactionElement } from '../types';

interface PatternPreview {
  success: boolean;
  iterations?: {
    values: any;
    rendered_latex: string;
  }[];
  error?: string;
  error_details?: string;
}

class PatternService {
  async getPatterns(container?: string | number | null): Promise<ExercisePattern[]> {
    const params: { container?: string | number } = {};
    if (container !== undefined) {
      params.container = container === null ? 'null' : container;
    }
    const response = await api.get('/exercise-patterns/', { params });
    // Handle both array and paginated response
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data.results) {
      return response.data.results;
    }
    return [];
  }

  async getAllPatterns(): Promise<ExercisePattern[]> {
    const response = await api.get('/exercise-patterns/', { params: { all: 'true' } });
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data.results) {
      return response.data.results;
    }
    return [];
  }

  async getPattern(id: number): Promise<ExercisePattern> {
    const response = await api.get<ExercisePattern>(`/exercise-patterns/${id}/`);
    return response.data;
  }

  async createPattern(data: Partial<ExercisePattern>): Promise<ExercisePattern> {
    const response = await api.post<ExercisePattern>('/exercise-patterns/', data);
    return response.data;
  }

  async updatePattern(id: number, data: Partial<ExercisePattern>): Promise<ExercisePattern> {
    // Use PATCH for partial updates (especially for public patterns)
    const response = await api.patch<ExercisePattern>(`/exercise-patterns/${id}/`, data);
    return response.data;
  }

  async deletePattern(id: number, keepPublic: boolean = false): Promise<void> {
    await api.delete(`/exercise-patterns/${id}/`, {
      data: { keep_public: keepPublic }
    });
  }

  async generateExercise(patternId: number): Promise<Exercise> {
    const response = await api.post<Exercise>(`/exercise-patterns/${patternId}/generate_exercise/`);
    return response.data;
  }

  async previewPattern(
    latexTemplate: string, 
    pythonGenerator: string, 
    iterations: number = 3
  ): Promise<PatternPreview> {
    try {
      const response = await api.post<PatternPreview>('/exercise-patterns/preview/', {
        latex_template: latexTemplate,
        python_generator: pythonGenerator,
        iterations,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur lors de la prévisualisation',
        error_details: error.response?.data?.details,
      };
    }
  }

  getPreviewPdfUrl(latexTemplate: string, pythonGenerator: string, iterations: number = 3): string {
    // For POST requests with body, we'll handle it differently
    return '/exercise-patterns/preview_pdf/';
  }

  async getAvailablePatterns(page: number = 1): Promise<{
    results: ExercisePattern[];
    count: number;
    next: string | null;
    previous: string | null;
  }> {
    const response = await api.get('/exercise-patterns/available/', {
      params: { page }
    });
    // Handle both paginated and non-paginated responses
    if (response.data.results) {
      return response.data;
    }
    // If not paginated (shouldn't happen now), wrap in paginated format
    return {
      results: response.data,
      count: response.data.length,
      next: null,
      previous: null
    };
  }

  async adoptPattern(patternId: number, containerId?: number | null): Promise<ExercisePattern> {
    const response = await api.post<ExercisePattern>(`/exercise-patterns/${patternId}/adopt/`, {
      container: containerId || null
    });
    return response.data;
  }

  async duplicatePattern(id: number): Promise<ExercisePattern> {
    const response = await api.post<ExercisePattern>(`/exercise-patterns/${id}/duplicate/`);
    return response.data;
  }

  async reorderPatterns(items: {id: number, order: number}[], container?: number | null): Promise<void> {
    await api.post('/exercise-patterns/reorder/', { items, container: container ?? null });
  }

  async testGenerator(patternId: number, pythonGenerator: string): Promise<{success: boolean, results?: any[], error?: string}> {
    try {
      const response = await api.post(`/exercise-patterns/${patternId}/test_generator/`, {
        python_generator: pythonGenerator
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur lors de l\'exécution du générateur'
      };
    }
  }

  async setThumbnail(patternId: number, selection: {page: number, x: number, y: number, width: number, height: number}): Promise<{success: boolean, thumbnail_url?: string, error?: string}> {
    try {
      const response = await api.post(`/exercise-patterns/${patternId}/set_thumbnail/`, selection);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur lors de la définition de la miniature'
      };
    }
  }

  async assistWithAi(prompt: string): Promise<{ content: string }> {
    const response = await api.post('/exercise-patterns/ai_assist/', { prompt });
    return response.data;
  }

  async addLibraryImage(patternId: number, imageId: number): Promise<ExercisePattern> {
    const response = await api.post(`/exercise-patterns/${patternId}/add_library_image/`, { image_id: imageId });
    return response.data.pattern;
  }

  async removeLibraryImage(patternId: number, imageId: number): Promise<ExercisePattern> {
    const response = await api.post(`/exercise-patterns/${patternId}/remove_library_image/`, { image_id: imageId });
    return response.data.pattern;
  }

  async importPattern(patternId: number, options: {
    container?: number | null;
    copy_solutions?: boolean;
    copy_skills?: boolean;
  }): Promise<ExercisePattern> {
    const response = await api.post<ExercisePattern>(`/exercise-patterns/${patternId}/import_pattern/`, options);
    return response.data;
  }

  async getRedactionElements(patternId: number): Promise<PatternRedactionElement[]> {
    const response = await api.get('/pattern-redaction-elements/', { params: { pattern: patternId } });
    if (Array.isArray(response.data)) return response.data;
    if (response.data?.results) return response.data.results;
    return [];
  }

  async createRedactionElement(data: Partial<PatternRedactionElement>): Promise<PatternRedactionElement> {
    const response = await api.post<PatternRedactionElement>('/pattern-redaction-elements/', data);
    return response.data;
  }

  async updateRedactionElement(id: number, data: Partial<PatternRedactionElement>): Promise<PatternRedactionElement> {
    const response = await api.patch<PatternRedactionElement>(`/pattern-redaction-elements/${id}/`, data);
    return response.data;
  }

  async deleteRedactionElement(id: number): Promise<void> {
    await api.delete(`/pattern-redaction-elements/${id}/`);
  }
}

const patternService = new PatternService();
export default patternService;